import "ag-grid-enterprise";
import React, { useEffect, useMemo, useRef } from "react";
import { Box, Text } from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type {
  CellClickedEvent,
  CellFocusedEvent,
  ColDef,
  GridApi,
  ICellRendererParams,
  RowClassRules,
  SelectionChangedEvent,
} from "ag-grid-enterprise";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { MmiBadge } from "../../../../components/MmiBadge";
import {
  selectAdvancedCopySelectedTargetRoomNumber,
  selectAdvancedCopySelectedTargetRoomNumbers,
  setAdvancedCopySelectedTargetRoomNumberInRedux,
  setAdvancedCopySelectedTargetRoomNumbersInRedux,
} from "../../../designSlice";
import {
  AdvancedCopyDevicesTableRow,
  selectAdvancedCopyDevicesTableRows,
} from "./advancedCopyDevicesSelectors";

const MmiCell: React.FC<ICellRendererParams<AdvancedCopyDevicesTableRow, number | null>> = (props) => {
  const value = props.value;

  if (value == null) {
    return (
      <Text size="sm" c="dimmed">
        -
      </Text>
    );
  }

  return <MmiBadge value={value} />;
};

const ExistingDevicesCell: React.FC<ICellRendererParams<AdvancedCopyDevicesTableRow, boolean>> = (
  props
) => {
  const value = Boolean(props.value);

  if (!value) {
    return (
      <Text size="sm" c="dimmed">
        Nei
      </Text>
    );
  }

  return (
    <Text size="sm" c="red.7" fw={600}>
      Ja
    </Text>
  );
};

export const AdvancedCopyDevicesSpacesTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<AgGridReact<AdvancedCopyDevicesTableRow>>(null);
  const rowData = useAppSelector(selectAdvancedCopyDevicesTableRows);
  const selectedTargetRoomNumber = useAppSelector(selectAdvancedCopySelectedTargetRoomNumber);
  const selectedTargetRoomNumbers = useAppSelector(selectAdvancedCopySelectedTargetRoomNumbers);

  const columnDefs = useMemo<ColDef<AdvancedCopyDevicesTableRow>[]>(
    () => [
      {
        headerName: "",
        colId: "selectedForCopy",
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        pinned: "left",
        lockPinned: true,
        sortable: false,
        filter: false,
        resizable: false,
        checkboxSelection: (params) => !Boolean(params.data?.hasExistingDevices),
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        showDisabledCheckboxes: true,
      },
      {
        headerName: "Romsnummer",
        field: "roomNumber",
        sortable: true,
        filter: true,
        minWidth: 150,
        flex: 1,
      },
      {
        headerName: "Romnavn",
        field: "roomName",
        sortable: true,
        filter: true,
        minWidth: 180,
        flex: 1.2,
      },
      {
        headerName: "Bygg",
        field: "building",
        sortable: true,
        filter: true,
        minWidth: 140,
        flex: 1,
      },
      {
        headerName: "Etasje",
        field: "floor",
        sortable: true,
        filter: true,
        minWidth: 140,
        flex: 1,
      },
      {
        headerName: "MMI",
        field: "mmi",
        sortable: true,
        filter: true,
        minWidth: 120,
        flex: 0.8,
        cellRenderer: MmiCell,
      },
      {
        headerName: "Himling MMI",
        field: "ceilingMmi",
        sortable: true,
        filter: true,
        minWidth: 140,
        flex: 0.9,
        cellRenderer: MmiCell,
      },
      {
        headerName: "Standardrom",
        field: "standardRoomNumber",
        sortable: true,
        filter: true,
        minWidth: 160,
        flex: 1,
      },
      {
        headerName: "Har komponenter fra før",
        field: "hasExistingDevices",
        sortable: true,
        filter: true,
        minWidth: 180,
        flex: 1,
        cellRenderer: ExistingDevicesCell,
      },
      {
        headerName: "Likhet ⓘ",
        field: "similarityScore",
        sortable: true,
        filter: "agNumberColumnFilter",
        minWidth: 130,
        flex: 0.8,
        headerTooltip:
          "Viser hvor lik romformen er sammenlignet med kilderommet. Verdien beregnes ved å sammenligne polygonene etter rotasjon og speiling.",
        valueFormatter: (params) => {
          const value = params.value as number | null | undefined;
          if (value == null) {
            return "-";
          }

          return `${Math.round(value * 100)} %`;
        },
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
    }),
    []
  );

  const rowClassRules = useMemo<RowClassRules<AdvancedCopyDevicesTableRow>>(
    () => ({
      "advanced-copy-row-copied": (params) => Boolean(params.data?.isCopiedInSession),
      "advanced-copy-row-target": (params) => params.data?.roomNumber === selectedTargetRoomNumber,
    }),
    [selectedTargetRoomNumber]
  );

  useEffect(() => {
    if (selectedTargetRoomNumber || rowData.length === 0) {
      return;
    }

    dispatch(setAdvancedCopySelectedTargetRoomNumberInRedux(rowData[0]?.roomNumber ?? null));
  }, [dispatch, rowData, selectedTargetRoomNumber]);

  useEffect(() => {
    const gridApi = gridRef.current?.api as GridApi<AdvancedCopyDevicesTableRow> | undefined;
    if (!gridApi) {
      return;
    }

    gridApi.forEachNode((node) => {
      const shouldBeSelected = selectedTargetRoomNumbers.includes(node.data?.roomNumber ?? "");
      if (node.isSelected() !== shouldBeSelected) {
        node.setSelected(Boolean(shouldBeSelected));
      }
    });
  }, [rowData, selectedTargetRoomNumbers]);

  useEffect(() => {
    const gridApi = gridRef.current?.api as GridApi<AdvancedCopyDevicesTableRow> | undefined;
    if (!gridApi || !selectedTargetRoomNumber) {
      return;
    }

    const selectedNode = gridApi.getRowNode(selectedTargetRoomNumber);
    if (selectedNode) {
      gridApi.ensureNodeVisible(selectedNode);
      gridApi.redrawRows({ rowNodes: [selectedNode] });
    }
  }, [selectedTargetRoomNumber]);

  const handleSelectionChanged = (event: SelectionChangedEvent<AdvancedCopyDevicesTableRow>) => {
    dispatch(
      setAdvancedCopySelectedTargetRoomNumbersInRedux(
        event.api
          .getSelectedNodes()
          .map((node) => node.data?.roomNumber)
          .filter((roomNumber): roomNumber is string => Boolean(roomNumber))
      )
    );
  };

  const handleCellFocused = (event: CellFocusedEvent<AdvancedCopyDevicesTableRow>) => {
    if (event.rowIndex == null) {
      return;
    }

    const focusedColumnId =
      typeof event.column === "string" ? event.column : event.column?.getColId();

    if (focusedColumnId === "selectedForCopy") {
      return;
    }

    const node = event.api.getDisplayedRowAtIndex(event.rowIndex);
    if (!node?.data?.roomNumber) {
      return;
    }

    dispatch(setAdvancedCopySelectedTargetRoomNumberInRedux(node.data.roomNumber));
  };

  const handleCellClicked = (event: CellClickedEvent<AdvancedCopyDevicesTableRow>) => {
    if (event.column?.getColId() === "selectedForCopy") {
      return;
    }

    dispatch(setAdvancedCopySelectedTargetRoomNumberInRedux(event.data?.roomNumber ?? null));
  };

  return (
    <Box>
      <style>
        {`
          .ag-theme-alpine .advanced-copy-row-copied {
            background-color: #e8f5e9 !important;
          }

          .ag-theme-alpine .advanced-copy-row-target .ag-cell {
            border-top: 1px solid #228be6;
            border-bottom: 1px solid #228be6;
          }
        `}
      </style>
      <div className="ag-theme-alpine" style={{ height: 360, width: "100%" }}>
        <AgGridReact<AdvancedCopyDevicesTableRow>
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows
          rowSelection="multiple"
          rowMultiSelectWithClick={false}
          suppressRowClickSelection
          isRowSelectable={(node) => !Boolean(node.data?.hasExistingDevices)}
          rowClassRules={rowClassRules}
          onSelectionChanged={handleSelectionChanged}
          onCellFocused={handleCellFocused}
          onCellClicked={handleCellClicked}
          getRowId={(params) => params.data.roomNumber}
        />
      </div>
    </Box>
  );
};
